import React, { useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { SurveyToast } from '@xiam/xiam-ui-toolkit'
import styles from './Dashboard.module.scss'
import MarginWrapper from '../../features/wrappers/MarginWrapper'
import { LoadingIcon } from '../../features/section-components/LoadingIcon'
import JNJFooter from '../../features/jnj-footer/JNJFooter'
import { ApplicationViewPort } from '../../features/ApplicationViewPort'
import { selectUserCallStatus } from '../../features/user/userSlice'
import { getUserAppsCallStatus } from '../../features/user-apps/UserAppsSlice'
import { SURVEY_TEMPLATE_TAG_HOME } from '../../util/config'
import { ToggleButtonGroup } from '../../features/section-components/ToggleButtonGroup'
import {
  fetchSurveyTemplates,
  selectSurveyTemplateByTag,
  selectSurveyTemplates,
} from '../Survey/surveyTemplateSlice'
import {
  resetSurveyResponses,
  setSurveyResponses,
  setIsSurveySubmitted,
  setCurrentPage,
  submitSurveyResponse,
} from '../Survey/surveySlice'

function DashboardDesktop({
  t, isPageLoading, userObjData, userAppsData, appGridIsLoading, isSnackbarOpen,
  setSnackbarOpen, isModalOpen, setModalOpen, isGridView, setIsGridView
}) {
  const userCallStatus = useSelector(selectUserCallStatus)
  const userAppsCallStatus = useSelector(getUserAppsCallStatus)
  const dispatch = useDispatch()
  const buttonColors = useMemo(() => ['green', '#D7AD01', 'red'], [])
  const appListStyle = isGridView ? '' : styles['app-list']

  const surveySchema = useSelector(
    selectSurveyTemplateByTag(SURVEY_TEMPLATE_TAG_HOME),
  )
  const surveyTemplate = useSelector(selectSurveyTemplates)

  const surveyResponses = useSelector((state) => state.survey.surveyResponses)
  const currentPage = useSelector((state) => state.survey.currentPage)
  const isSurveySubmitted = useSelector((state) => state.survey.isSurveySubmitted)
  const templateId = surveySchema?.template_id
  const [localResponses, setLocalResponses] = useState(surveyResponses)

  const preferredLanguage = userObjData?.preferredLanguage || 'en-US'

  const onCloseReset = () => {
    dispatch(setIsSurveySubmitted(false))
    dispatch(resetSurveyResponses())
    dispatch(setCurrentPage(0))
    setLocalResponses({})
  }

  useEffect(() => {
    dispatch(fetchSurveyTemplates())
  }, [dispatch])

  const handleSurveyChange = (newResponses) => {
    dispatch(setSurveyResponses(newResponses))
  }

  const handleAnswerChange = (questionId, answer) => {
    const updatedResponses = { ...localResponses, [questionId]: answer }
    setLocalResponses(updatedResponses)
    handleSurveyChange(updatedResponses)
  }

  const handleCurrentPageChange = (newPage) => {
    dispatch(setCurrentPage(newPage))
  }

  const handleSurveySubmit = (formattedSurveyResponse) => {
    if (!templateId) return

    dispatch(
      submitSurveyResponse({
        id: templateId,
        responses: formattedSurveyResponse,
      }),
    )
      .unwrap()
      .then(() => {
        dispatch(setIsSurveySubmitted(true))
        dispatch(resetSurveyResponses())
        setSnackbarOpen(false)
      })
  }

  return (
    <MarginWrapper>
      <div className={styles['dashboard-page']}>
        <div className={styles['welcome-header']}>
          <Typography
            sx={{
              fontFamily: "'JohnsonDisplay', Arial", fontWeight: 'bolder', color: 'black', lineHeight: '2', whiteSpace: 'nowrap', fontSize: 'calc(16px + 1vw)',
            }}
          >
            {`${t('WelcomeHeader')} ${userObjData.firstName || ''}! \n`}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'JohnsonDisplay', Arial", color: 'black', lineHeight: '0', paddingBottom: 5, whiteSpace: 'nowrap', fontSize: 'calc(16px + 1vw)',
            }}
          >
            {`${t('ApplicationHeader')}`}
          </Typography>
        </div>

        <div className={styles['toggle-search-section']}>
          <div className={styles['toggle-search-container']}>
            {userAppsData.length ? (
              <ToggleButtonGroup className={styles['toggle-button-group']} falseText="List View" trueText="Grid View" value={isGridView} onClick={() => setIsGridView(!isGridView)} />
            ) : ''}
          </div>
        </div>
        <LoadingIcon isLoading={isPageLoading || userCallStatus === 'failed'}>
          {!(userAppsCallStatus === 'succeeded' && !userAppsData.length) ? (
            <div className={styles[isGridView ? 'middle-section' : 'middle-section-list']}>
              <LoadingIcon isLoading={appGridIsLoading}>
                <div className={`${styles['app-grid']} ${appListStyle}`}>
                  <ApplicationViewPort applications={userAppsData} noAppsMsg={t('NoAppsMsg')} errorContactMsg={t('ErrorContactMsg')} showLabels="true" isGridView={isGridView} />
                </div>
              </LoadingIcon>
            </div>
          ) : (
            <div className={styles['middle-section-no-apps']}>
              <ApplicationViewPort applications={userAppsData} noAppsMsg={t('NoAppsMsg')} errorContactMsg={t('ErrorContactMsg')} showLabels="true" />
            </div>
          )}
          <div>
            {surveySchema && (
            <SurveyToast
              isSnackbarOpen={isSnackbarOpen}
              setSnackbarOpen={setSnackbarOpen}
              modalOpen={isModalOpen}
              setModalOpen={setModalOpen}
              buttonColors={buttonColors}
              isDelayed
              surveyResponses={surveyResponses}
              setSurveyResponses={setSurveyResponses}
              surveyTemplate={surveyTemplate}
              SURVEY_TEMPLATE_TAG={SURVEY_TEMPLATE_TAG_HOME}
              preferredLanguage={preferredLanguage}
              responses={localResponses}
              handleAnswerChange={handleAnswerChange}
              handleSubmit={handleSurveySubmit}
              handleCurrentPageChange={handleCurrentPageChange}
              currentPage={currentPage}
              onCloseReset={onCloseReset}
              isSurveySubmitted={isSurveySubmitted}
            />
            )}
          </div>
          <div className={styles['jnj-footer']}>
            <JNJFooter text={t('FooterText')} footerSize="medium" />
          </div>
        </LoadingIcon>
      </div>
    </MarginWrapper>
  )
}

DashboardDesktop.propTypes = {
  t: PropTypes.func.isRequired,
  isPageLoading: PropTypes.bool.isRequired,
  userObjData: PropTypes.shape({
    login: PropTypes.string,
    status: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    preferredLanguage: PropTypes.string,
    secondEmail: PropTypes.string,
    displayName: PropTypes.string,
    created: PropTypes.string.isRequired,
    lastLogin: PropTypes.string.isRequired,
  }).isRequired,
  userAppsData: PropTypes.arrayOf(PropTypes.shape({
    appId: PropTypes.string.isRequired,
    appName: PropTypes.string.isRequired,
  })).isRequired,
  appGridIsLoading: PropTypes.bool.isRequired,
  isSnackbarOpen: PropTypes.bool.isRequired,
  setSnackbarOpen: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  isGridView: PropTypes.bool.isRequired,
  setIsGridView: PropTypes.func.isRequired
}

export default React.memo(DashboardDesktop)
